import { useEffect, useMemo, useState } from "react";
import { Link, Outlet, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { authClient, canAccessCommandCenter, fetchMe, type Me } from "../auth";
import { api, scopeQuery, ScopeContext, type Scope } from "../api";
import { useT } from "../i18n";
import { StaffActivate } from "./StaffActivate";
import { useTheme } from "../theme";
import { HeyLifeLogo, HeyLifeTile } from "../components/Brand";
import { bindSentryUserContext, useSentryUserContext } from "../sentry";

const NAV = ["today", "attention", "pulse", "content", "questions", "prayer", "moderation", "people", "fams", "team", "decisions", "rewards", "notifications", "settings"] as const;
const SCOPE_KEY = "heylife.admin.scope";

export function useMe() {
  return useQuery({ queryKey: ["me"], queryFn: fetchMe, staleTime: 30_000 });
}

/** Staff who accepted an invite and have not confirmed access yet: membership exists, active = false. */
function isPendingStaff(me: Me): boolean {
  return !me.isSuperadmin && Boolean(me.membership && !me.membership.active && (me.membership.role === "admin" || me.membership.role === "pastor"));
}

function useScopeState(me: Me | null | undefined): Scope {
  const canPick = Boolean(me?.isSuperadmin);
  const [churchId, setChurch] = useState<string | null>(() => {
    const s = localStorage.getItem(SCOPE_KEY);
    return s && s !== "global" ? s : null;
  });
  const churches = useQuery({
    queryKey: ["admin", "churches", "list"],
    queryFn: () => api<{ churches: Array<{ id: string; name: string; slug: string; status: string }> }>("/admin/churches"),
    enabled: canPick,
    staleTime: 60_000,
  });
  return useMemo<Scope>(
    () => ({
      churchId: canPick ? churchId : (me?.membership?.churchId ?? null),
      churches: churches.data?.churches ?? [],
      canPickScope: canPick,
      setChurchId: (id) => {
        localStorage.setItem(SCOPE_KEY, id ?? "global");
        setChurch(id);
      },
    }),
    [canPick, churchId, churches.data, me?.membership?.churchId],
  );
}

export function AdminShell() {
  const { t } = useT();
  const me = useMe();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const scope = useScopeState(me.data);
  useSentryUserContext(me.data?.userId, scope.churchId);
  const [theme, setTheme] = useTheme();
  const caps = useQuery({
    queryKey: ["admin", "capabilities", scope.churchId],
    queryFn: () => api<{ admin: Record<string, boolean> }>(`/admin/capabilities${scopeQuery(scope, "church")}`),
    enabled: Boolean(me.data?.membership || me.data?.isSuperadmin),
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!me.isLoading && !me.isFetching && !me.data) void navigate({ to: "/admin/login", search: { step: undefined } });
  }, [me.isLoading, me.isFetching, me.data, navigate]);

  if (me.isLoading || !me.data) return <div className="auth-page" />;
  const staff = me.data;

  const signOut = async () => {
    bindSentryUserContext(null, null);
    await authClient.signOut();
    await qc.invalidateQueries({ queryKey: ["me"] });
    await navigate({ to: "/admin/login", search: { step: undefined } });
  };

  if (isPendingStaff(staff)) return <StaffActivate onDone={() => qc.invalidateQueries({ queryKey: ["me"] })} onSignOut={signOut} />;

  if (!canAccessCommandCenter(staff)) {
    return (
      <div className="auth-page">
        <div className="card">
          <span className="brand">
            <HeyLifeTile size={40} />
            Hey Life
          </span>
          <h1>{t("admin.login.title")}</h1>
          <p className="muted">{t("admin.login.noAccess")}</p>
          <button className="btn secondary" onClick={signOut}>
            {t("admin.login.signOut")}
          </button>
        </div>
      </div>
    );
  }

  const nav = [...NAV, ...(staff.isSuperadmin ? (["churches"] as const) : [])].filter((k) => staff.isSuperadmin || k === "churches" || caps.data?.admin[k] !== false);

  return (
    <ScopeContext.Provider value={scope}>
      <div className="cc">
        <nav>
          <Link className="brand" to="/admin" aria-label="Hey Life">
            <HeyLifeLogo height={22} duotone />
          </Link>
          {scope.canPickScope ? (
            <label className="scope">
              <span>{t("admin.scope.label")}</span>
              <select value={scope.churchId ?? "global"} onChange={(e) => scope.setChurchId(e.target.value === "global" ? null : e.target.value)}>
                <option value="global">{t("admin.scope.global")}</option>
                {scope.churches.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {nav.map((k) => (
            <NavLink key={k} k={k} label={t(`admin.nav.${k}`)} />
          ))}
          <div className="spacer" />
          <button type="button" className="theme-toggle" onClick={() => setTheme(theme === "dark" ? "light" : "dark")} aria-pressed={theme === "dark"}>
            {theme === "dark" ? t("admin.theme.light") : t("admin.theme.dark")}
          </button>
          <div className="who">
            <div>{staff.profile.fullName ?? staff.profile.alias}</div>
            <div className="muted">{staff.church?.name ?? t("admin.home.platform")}</div>
            <button className="btn secondary small ghost" onClick={signOut}>
              {t("admin.login.signOut")}
            </button>
          </div>
        </nav>
        <main>
          <Outlet />
        </main>
      </div>
    </ScopeContext.Provider>
  );
}

function NavLink({ k, label }: { k: string; label: string }) {
  switch (k) {
    case "today":
      return (
        <Link to="/admin" activeOptions={{ exact: true }}>
          {label}
        </Link>
      );
    case "content":
      return (
        <Link to="/admin/content" search={{ tab: "dailyWords" }}>
          {label}
        </Link>
      );
    case "questions":
      return <Link to="/admin/questions">{label}</Link>;
    case "attention":
      return <Link to="/admin/attention">{label}</Link>;
    case "moderation":
      return <Link to="/admin/moderation">{label}</Link>;
    case "prayer":
      return <Link to="/admin/prayer">{label}</Link>;
    case "fams":
      return <Link to="/admin/fams">{label}</Link>;
    case "pulse":
      return <Link to="/admin/pulse">{label}</Link>;
    case "settings":
      return <Link to="/admin/settings">{label}</Link>;
    case "people":
      return <Link to="/admin/people">{label}</Link>;
    case "notifications":
      return (
        <Link to="/admin/notifications" search={{ tab: "notificationMatrix" }}>
          {label}
        </Link>
      );
    case "rewards":
      return <Link to="/admin/rewards">{label}</Link>;
    case "team":
      return <Link to="/admin/team">{label}</Link>;
    case "decisions":
      return <Link to="/admin/decisions">{label}</Link>;
    case "churches":
      return <Link to="/admin/churches">{label}</Link>;
    default:
      return (
        <Link to="/admin/$section" params={{ section: k }} className="soon">
          {label}
        </Link>
      );
  }
}

export function AdminSection({ section }: { section: string }) {
  const { t } = useT();
  return (
    <>
      <h1>{t(`admin.nav.${section}`)}</h1>
      <p className="muted">{t("admin.home.comingSoon")}</p>
    </>
  );
}
